import { SessionGroup } from './inventoryOverviewUtils';

export interface InventoryComparison {
  endSessionKey: string | null;
  baselineSessionKey: string | null;
}

export const comparisonGroupKey = (session: SessionGroup): string =>
  session.groupId ?? session.groupName ?? 'ungrouped';

/** Legacy date buckets can contain several groups; they are not single inventory counts. */
export const isSingleGroupComparisonSession = (session: SessionGroup): boolean =>
  !session.key.startsWith('legacy:') || new Set(session.counts.map(count =>
    count.groupId ?? count.groupNameSnapshot ?? 'ungrouped',
  )).size <= 1;

/** Count Review can address UUID sessions, or the latest count of a group without a UUID. */
export function isReviewEndSession(session: SessionGroup, sessions: SessionGroup[]): boolean {
  return !!session.counts[0]?.sessionId || (!!session.groupId && !sessions.some(other =>
    comparisonGroupKey(other) === comparisonGroupKey(session) && other.completedAt > session.completedAt,
  ));
}

/** Resolve a single inventory window; never combine counts from different groups. */
export function resolveInventoryComparison(
  sessions: SessionGroup[],
  groupId: string | null,
  choice?: Partial<InventoryComparison> | null,
  options: { review?: boolean } = {},
): { end: SessionGroup | null; baseline: SessionGroup | null; earlier: SessionGroup[] } {
  const candidates = sessions
    // Count Review retains its backend's historical automatic predecessor semantics.
    // Leakage must never aggregate a legacy bucket spanning different groups.
    .filter(session => options.review || isSingleGroupComparisonSession(session))
    .filter(session => groupId === null || comparisonGroupKey(session) === groupId)
    .slice().sort((a, b) => b.completedAt.getTime() - a.completedAt.getTime());
  const endpoints = options.review ? candidates.filter(session => isReviewEndSession(session, candidates)) : candidates;
  const end = endpoints.find(session => session.key === choice?.endSessionKey) ?? endpoints[0] ?? null;
  const older = end ? candidates.filter(session =>
    comparisonGroupKey(session) === comparisonGroupKey(end) && session.completedAt < end.completedAt,
  ) : [];
  // A legacy immediate predecessor is represented by the API's automatic baseline
  // (no baselineSessionId). Older legacy buckets cannot be selected by that API.
  const earlier = options.review ? older.filter((session, index) => index === 0 || !!session.counts[0]?.sessionId) : older;
  const baseline = earlier.find(session => session.key === choice?.baselineSessionKey) ?? earlier[0] ?? null;
  return { end, baseline, earlier };
}
